import React, { useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle, Sparkles, Timer, Undo2 } from 'lucide-react';
import MarkdownText from '../MarkdownText';
import { formatMinutes, formatSeconds } from '../format';
import { buildTimeline, lastRunState } from '../timeline';
import StepCard from './StepCard';

function RunSummary({ item, actions, readOnly, onUndoRun }) {
  const saved = item.timeSaved || {};
  const manual = saved.manualMinutes || 0;
  const agentSeconds = saved.agentSeconds || 0;
  const share = manual > 0 ? Math.max(1.5, Math.min(100, (agentSeconds / 60 / manual) * 100)) : 0;
  const undoable = actions.filter((action) => action.canUndo).length;

  return (
    <div className="dagent-summary">
      <div className="dagent-summary__row">
        <Timer size={15} aria-hidden="true" />
        <span>
          Done in <strong>{formatSeconds(agentSeconds)}</strong>
        </span>
        {manual > 0 && (
          <span className="dagent-summary__manual">
            about <strong>{formatMinutes(manual)}</strong> of desk work
          </span>
        )}
        {saved.actionsExecuted > 0 && (
          <span className="dagent-summary__count">
            {saved.actionsExecuted} action{saved.actionsExecuted === 1 ? '' : 's'} executed
          </span>
        )}
      </div>
      {manual > 0 && (
        <div className="dagent-summary__bars" aria-hidden="true">
          <div className="dagent-summary__bar dagent-summary__bar--manual" />
          <div className="dagent-summary__bar dagent-summary__bar--agent" style={{ width: `${share}%` }} />
        </div>
      )}
      {undoable > 0 && !readOnly && (
        <button type="button" className="dagent-link-btn" onClick={() => onUndoRun(item.runId)}>
          <Undo2 size={13} aria-hidden="true" /> Undo everything from this run ({undoable})
        </button>
      )}
    </div>
  );
}

function Conversation({ controller, emptyState }) {
  const { agentSession, undoRun } = controller;
  const { events, actions, runs, session, readOnly, decide, undoAction } = agentSession;
  const items = useMemo(() => buildTimeline(events), [events]);
  const runsById = useMemo(() => new Map(runs.map((run) => [run.id, run])), [runs]);
  const scroller = useRef(null);
  const stickToBottom = useRef(true);

  useEffect(() => {
    const element = scroller.current;
    if (element && stickToBottom.current) element.scrollTop = element.scrollHeight;
  }, [items.length, events.length, actions]);

  const onScroll = () => {
    const element = scroller.current;
    if (element) stickToBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
  };

  const handleDecide = async (runId, ids) => {
    try {
      await decide(runId, ids);
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const handleUndo = async (actionId, force = false) => {
    try {
      const response = await undoAction(actionId, force);
      const outcome = response?.outcome;
      if (outcome && !outcome.ok) {
        toast.warn(outcome.conflict ? `${outcome.error}. Use "Force undo" to override.` : outcome.error);
        return false;
      }
      toast.success('Undone');
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    }
  };

  const handleUndoBatch = async (ids) => {
    for (const id of [...ids].reverse()) {
      // eslint-disable-next-line no-await-in-loop
      await handleUndo(id);
    }
  };

  const working = session?.status === 'RUNNING' && lastRunState(events) !== 'finished';

  return (
    <div className="dagent-conversation" ref={scroller} onScroll={onScroll}>
      {items.length === 0 && !working && emptyState}
      {items.map((item) => {
        switch (item.kind) {
          case 'user':
            return (
              <div key={item.key} className="dagent-msg dagent-msg--user">
                {item.contextTag && <div className="dagent-msg__context">{item.contextTag.replace(/^\[Viewing: /, '').replace(/\]$/, '')}</div>}
                <div className="dagent-msg__bubble">{item.text}</div>
              </div>
            );
          case 'assistant':
            return (
              <div key={item.key} className="dagent-msg dagent-msg--agent">
                <span className="dagent-msg__avatar">
                  <Sparkles size={13} aria-hidden="true" />
                </span>
                <div className="dagent-msg__body">
                  <MarkdownText text={item.text} />
                </div>
              </div>
            );
          case 'step': {
            const run = runsById.get(item.runId);
            return (
              <StepCard
                key={item.key}
                step={item}
                actions={actions.filter((action) => action.batchId === item.toolCallId)}
                canDecide={!readOnly && run?.status === 'AWAITING_APPROVAL'}
                onDecide={handleDecide}
                onUndoAction={readOnly ? null : handleUndo}
                onUndoBatch={readOnly ? null : handleUndoBatch}
              />
            );
          }
          case 'note':
            return (
              <div key={item.key} className={`dagent-note dagent-note--${item.tone}`}>
                {item.text}
              </div>
            );
          case 'run-summary':
            return (
              <RunSummary
                key={item.key}
                item={item}
                actions={actions.filter((action) => action.runId === item.runId)}
                readOnly={readOnly}
                onUndoRun={undoRun}
              />
            );
          case 'run-failed':
            return (
              <div key={item.key} className="dagent-note dagent-note--error">
                <AlertTriangle size={14} aria-hidden="true" /> {item.error}
              </div>
            );
          case 'run-cancelled':
            return (
              <div key={item.key} className="dagent-note">
                {item.text}
              </div>
            );
          default:
            return null;
        }
      })}
      {working && (
        <div className="dagent-working" role="status">
          <span className="dagent-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          Working
        </div>
      )}
    </div>
  );
}

export default Conversation;
